<template>
    <AboutHead :headline-blue="$t('team_sub')" :headline="$t('team_head')" />

    <transition :name="$route.meta.transition || 'fade'">
        <div class="relative lg:-top-[80px]">
            <div>
                <div class="container mx-auto min-h-screen">
                    <div v-if="Object.keys(teams).length > 0">
                        <div v-for="(team, teamId) in teams" :key="teamId" class="team">
                            <h2 class="text-2xl text-black mt-[60px] mb-[30px] !font-bold" data-aos="fade-right">
                                {{ $translate(team.name) }}
                            </h2>
                            <div class="flex flex-col gap-y-4">
                                <div v-for="(members, memberId) in team.splitMembers" :key="memberId" class="team">
                                    <ul>
                                        <TeamPortraits
                                            v-model:active-top-post="activePortrait"
                                            data-aos="fade-left"
                                            :members="members"
                                            height="650px"
                                            :width-active="'lg:w-5/12'"
                                            :width-unactive="'lg:w-4/12'"
                                            :mobile-carousel="false"
                                        />
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="region === 'de'" class="w-full text-center my-28">
                <div class="mb-10 lg:md-0">
                    <AboutHead
                        headline="Werde Teil des Teams"
                        subline="Wir bei HKCM sind stets auf der Suche nach engagierten neuen Kollegen.
                        Bewerben Sie sich jetzt und werden Sie Teil unseres Teams.
                        Unsere aktuellen Jobangebote finden Sie hier."
                    />
                </div>

                <div v-if="youtube" class="m-auto lg:w-[560px] w-11/12 h-48 sm:h-[400px] md:h-[500px] lg:h-[316px]">
                    <iframe
                        width="560"
                        height="316"
                        src="https://www.youtube-nocookie.com/embed/8atZtsEgkPM?si=9_BOb4NaE0jPkrwY&amp;controls=0"
                        title="YouTube video player"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerpolicy="strict-origin-when-cross-origin"
                        allowfullscreen
                    >
                    </iframe>
                </div>
                <div
                    v-else
                    class="m-auto lg:w-[560px] w-11/12 h-48 sm:h-[400px] md:h-[500px] lg:h-[316px] bg-gray-100 rounded-lg flex flex-col items-center justify-center p-4 text-center"
                >
                    <div class="w-16 h-16 rounded-full bg-white shadow flex items-center justify-center">
                        <i class="fi fi-rr-play text-gray-400 text-3xl"></i>
                    </div>
                    <p class="text-sm text-gray-600 font-medium">{{ $t('scampage.blockedVideo') }}</p>
                    <CookieReset />
                </div>

                <a class="btn w-full lg:w-auto primary lg:xl text-xs md:text-base" href="https://hopf-klinkmueller-capital-management.workwise.io/" target="_blank">
                    {{ $t('Werde ein Teil von HKCM') }}
                </a>
            </div>
            <div v-for="(env, index) in envVariables" :key="index" style="display: none">{{ index }} {{ env }}</div>
        </div>
    </transition>
</template>

<script>
import CookieReset from '../components/CookieReset.vue';
import { useCcm19Global } from '../useCcm19Global';

export default {
    components: { CookieReset },
    setup() {
        const { onReady, youtube } = useCcm19Global();
        onReady(() => {});

        return { youtube };
    },
    data() {
        return {
            search: null,
            loading: true,
            activePortrait: null,
            teams: [],
            maxMembersEachRow: 7,
            envVariables: []
        };
    },

    computed: {
        region() {
            return window.meta.region_configuration.region || 'unknown';
        }
    },

    async mounted() {
        await this.fetchTeams();
        this.envVariables.push({
            name: 'VITE_CHECKOUT_ALLOWED',
            value: import.meta.env.VITE_CHECKOUT_ALLOWED ?? '',
            type: typeof import.meta.env.VITE_CHECKOUT_ALLOWED ?? ''
        });
        this.envVariables.push({
            name: 'VITE_SHOW_LANGUAGE_SWITCH',
            value: import.meta.env.VITE_SHOW_LANGUAGE_SWITCH ?? '',
            type: typeof import.meta.env.VITE_SHOW_LANGUAGE_SWITCH ?? ''
        });
    },

    methods: {
        async fetchTeams() {
            this.loading = true;
            try {
                await this.$store.dispatch('aboutus/index');
                this.teams = this.$store.getters['aboutus/all'];
                this.splitMembers();
            } catch (error) {
                console.error('Error loading team data:', error);
            } finally {
                this.loading = false;
            }
        },

        splitMembers() {
            // Global counter for unique indexing
            let totalIndex = 0;

            Object.values(this.teams).forEach((team) => {
                const members = [...team.members]; // Copy the array
                const result = [];

                while (members.length > 0) {
                    let chunkSize;

                    // find manually line brake
                    const splitIndex = members.findIndex((member) => member.row_break);

                    if (splitIndex !== -1) {
                        chunkSize = splitIndex + 1;
                    } else if (members.length <= this.maxMembersEachRow) {
                        chunkSize = members.length;
                    } else {
                        const remaining = members.length;
                        const optimalRows = Math.ceil(remaining / this.maxMembersEachRow);
                        chunkSize = Math.ceil(remaining / optimalRows);
                    }

                    const chunk = members.splice(0, chunkSize);

                    // Assign a unique totalIndex and rowindex to each member
                    chunk.forEach((member, rowIndex) => {
                        member.totalIndex = totalIndex++;
                        member.rowIndex = rowIndex;
                    });

                    result.push(chunk);
                }

                for (let i = 0; i < result.length - 1; i++) {
                    // Check if the last member of the current row has set a manual line break (higher priority)
                    // If not, check if the next row has more members, then keep higher amount of images in the next row
                    if (!result[i][result[i].length - 1].row_break && result[i].length > result[i + 1].length) {
                        result[i + 1].unshift(result[i].pop());
                    }
                }

                team.splitMembers = result;
            });
        }
    }
};
</script>
